// @flow
import * as Immutable from 'immutable';
import { entityMasterSingleEditFactory } from 'common/entity/generic/SingleEntityEditContainer';
import DayWeekSelector from 'common/entity/generic/DayWeekSelector';
import FieldWithSwitch, {
    fieldWithSwitchFactory
} from 'common/entity/generic/FieldWithSwitch';
import RiderIdInput from './RiderIdInput';
import VisitFrequencyInfo from './VisitFrequencyInfo';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { ClientSelectors } from 'common/redux/selectors/clientSelector';
import { SalesCategoryId } from 'common/models/internal/SalesCategoryId';
import {
    cities as citiesApi,
    pincodes as pincodesApi,
    outlet as outletApi,
    salesCategorySearch
} from 'common/middleware/api';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import getSymbolFromCurrency from 'currency-symbol-map';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import CodeInputWithId from 'common/entity/generic/CodeInputWithId';
import HolidaySelector from 'common/entity/generic/HolidaySelector';

import DataUtils from 'common/utils/DataUtils';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import OutletActions from 'common/redux/actions/outlet';
import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import { getWeekValues } from './OutletMasterContainer';
import { ChannelData } from 'common/models/internal/ChannelData';
import CountryHelper from 'common/helpers/CountryHelper';
import locationMasterIDs from 'common/tests/masterManagement/locationMaster.json';
import {
    OutletLocationTypeEnum,
    MasterOutlet
} from 'common/models/internal/MasterOutlet';
import { ENTITY_NAME_ID } from 'common/constants/entity/outletMaster';

import outletMasterIds from 'common/tests/masterManagement/outletMaster.json';
import { MAX_DURATION_FREQUENCY_IN_WEEKS } from './tableConfig';

const RiderIdInputWithSwitch = fieldWithSwitchFactory({
    DependentField: RiderIdInput
});

const DEFAULT_VISIT_FREQUENCY_INFO = {
    spaceVisitsEvenly: false,
    repeatCount: 1,
    duration: 7
};

const weekInfo = ({ t }) => {
    return Array(MAX_DURATION_FREQUENCY_IN_WEEKS)
        .fill(1)
        .map((_, index) => ({
            label: t('outletTable.week', {
                count: index + 1
            }),
            value: `WEEK${index + 1}`
        }));
};

const formatCreateLabel = t => label => {
    return t('outletForm.createPrompt', {
        code: label
    });
};

const formatLocationCreateLabel = t => label => {
    return t('outletForm.createLocationCode', { label });
};

// Filter for EntityLocationCodeInput field
const defaultActiveFilter = new FilterBuilder()
    .withName('status')
    .withOperation(FilterOperation.Equals)
    .withValue('ACTIVE');

const locationCodeInputFilters = ({ entity, input }) => {
    let filters = new FilterListBuilder().add(defaultActiveFilter);
    if (entity.teams && entity.teams.size > 0) {
        filters = filters.add(
            new FilterBuilder()
                .withName('teams')
                .withOperation(FilterOperation.Contains)
                .withValues(entity.teams.toJS())
                .withCaseSensitive(true)
        );
    }
    if (!DataUtils.isEmpty(input)) {
        filters = filters.add(
            new FilterBuilder()
                .withName('code')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(input)
        );
    }

    return filters.build();
};

// Filters for RiderInputId field
const DEFAULT_RIDER_FILTERS = [
    new FilterBuilder()
        .withName('status')
        .withOperation(FilterOperation.Equals)
        .withValue('ACTIVE')
];

const riderIdInputFilters = ({ entity, input }) => {
    let filters = new FilterListBuilder().addAll(DEFAULT_RIDER_FILTERS);
    if (entity.teams && entity.teams.size > 0) {
        filters = filters.add(
            new FilterBuilder()
                .withName('teams')
                .withOperation(FilterOperation.Contains)
                .withValues(entity.teams.toJS())
                .withCaseSensitive(true)
        );
    }
    if (!DataUtils.isEmpty(input)) {
        filters = filters.add(
            new FilterBuilder()
                .withName('id')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(input)
        );
    }
    return filters.build();
};

// Possible values for fields from outletSettings
const getPossibleValues = ({ key, props }) => {
    return (
        props.entitySettings?.getPossibleValuesForModelField(
            'outletSettings',
            key
        ) || []
    );
};

const OUTLET_FIELDS = [
    {
        accessor: 'code',
        label: 'outletForm.code',
        helpText: 'outletForm.codeTooltip',
        disabledOnEdit: true,
        required: true,
        type: FieldTypeEnum.Text,
        placeholder: 'outletForm.codePlaceholder',
        component: CodeInputWithId,
        testId: outletMasterIds.addNewOutlet.inputCode,
        extraProps: ({ entity, field, props }) => {
            const {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                clientId
            } = props;
            return {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                id: entity.getIn(['id']),
                code: entity.getIn(field.absoluteAccessor),
                clientId
            };
        },
        onChangeHandler: ({ entity, value }) => {
            const { id, code } = value;
            if ('id' in value) {
                return entity.setId(id);
            } else if ('code' in value) {
                return entity.setCode(code);
            }
        }
    },
    {
        accessor: 'name',
        label: 'outletForm.name',
        required: true,
        helpText: 'outletForm.nameTooltip',
        type: FieldTypeEnum.Text,
        placeholder: 'outletForm.namePlaceholder',
        testId: outletMasterIds.addNewOutlet.inputName
    },
    {
        accessor: 'teams',
        label: 'outletForm.team',
        required: true,
        helpText: 'outletForm.teamTooltip',
        type: FieldTypeEnum.Team,
        testId: outletMasterIds.addNewOutlet.inputTeam,
        extraProps: () => {
            return {
                interfaceAsTeamList: true
            };
        }
    },
    {
        accessor: 'salesCategoryId',
        type: FieldTypeEnum.Select,
        label: 'outletForm.salesCategory',
        placeholder: 'outletForm.salesCategoryPlaceholder',
        helpText: 'outletForm.salesCategoryTooltip',
        testId: outletMasterIds.addNewOutlet.inputCategory,
        options: ({ field }) => {
            return field.fetchInfo?.response?.salesCategories?.map(
                ({ id, name }) => ({
                    value: id,
                    label: name
                })
            );
        },
        getProcessedValueForField: ({ entity, field }) => {
            return entity.getIn(field.absoluteAccessor)?.salesCategoryId;
        },
        required: true,
        onChangeHandler: ({ field, entity, value }) => {
            const category = value
                ? SalesCategoryId.fromJS({
                      salesCategoryId: value.value,
                      clientId: entity.clientId
                  })
                : null;
            return entity.setIn(field.absoluteAccessor, category);
        },
        fetchInfo: {
            fetcher: ({ params, body }) =>
                salesCategorySearch.post(params, body),
            params: ({ props }) => {
                const defaultActiveFilter = new FilterListBuilder()
                    .add(
                        new FilterBuilder()
                            .withName('status')
                            .withOperation(FilterOperation.Equals)
                            .withValue('ACTIVE')
                    )
                    .build();
                return {
                    params: { clientId: props.clientId },
                    body: { filters: defaultActiveFilter }
                };
            },
            response: null
        }
    },
    {
        accessor: 'locationInfo.location',
        dependentAccessors: [
            'locationInfo.location.address.formattedAddress',
            'locationInfo.location.address.localityName',
            'locationInfo.location.address.city',
            'locationInfo.location.address.state',
            'locationInfo.location.address.pincode',
            'locationInfo.location.address.countryCode',
            'locationInfo.location.latLng',
            'locationInfo.location.latLng.lat',
            'locationInfo.location.latLng.lng',
            'locationInfo.location.type'
        ],
        label: 'outletForm.parentOutletCode',
        required: true,
        disabledOnEdit: true,
        placeholder: 'outletForm.locationSearchByText',
        type: FieldTypeEnum.OutletLocationCodeInput,
        extraProps: ({ field, entity, t, props }) => {
            const location = entity.getIn(field.absoluteAccessor);
            const value = location?.name ? location : null;
            return {
                teams: entity.getIn(['teams']),
                clientId: props.clientId,
                name: entity.getIn(['name']),
                existingOutletLocation: entity.getIn(field.absoluteAccessor),
                formatCreateLabel: formatLocationCreateLabel(t),
                selectedLocation: value,
                popOverContent: location?.getValidationMessage('code'),
                getLocationFilters: locationCodeInputFilters
            };
        }
    },
    {
        accessor: 'locationInfo.location.address.formattedAddress',
        dependentAccessors: [
            'locationInfo.location.latLng',
            'locationInfo.location.latLng.lat',
            'locationInfo.location.latLng.lng'
        ],
        supportsOnBlur: true,
        label: 'outletForm.address.address',
        required: true,
        type: FieldTypeEnum.Text,
        testId: outletMasterIds.addNewOutlet.inputAddress.inputFormattedAdd,
        placeholder: 'outletForm.address.addressPlaceholder'
    },
    {
        accessor: 'locationInfo.location.address.localityName',
        dependentAccessors: [
            'locationInfo.location.latLng',
            'locationInfo.location.latLng.lat',
            'locationInfo.location.latLng.lng'
        ],
        supportsOnBlur: true,
        label: 'outletForm.address.locality',
        type: FieldTypeEnum.Text,
        placeholder: 'outletForm.address.localityPlaceholder',
        halfWidth: true,
        testId: outletMasterIds.addNewOutlet.inputAddress.inputLocality
    },
    {
        accessor: 'locationInfo.location.address.city',
        dependentAccessors: [
            'locationInfo.location.latLng',
            'locationInfo.location.latLng.lat',
            'locationInfo.location.latLng.lng'
        ],
        testId: outletMasterIds.addNewOutlet.inputAddress.inputCity,
        supportsOnBlur: true,
        placeholder: 'outletForm.address.cityPlaceholder',
        label: 'outletForm.address.city',
        type: FieldTypeEnum.City,
        halfWidth: true,
        options: ({ field, props }) => {
            return {
                data: field.fetchInfo.response,
                countryCode: props.defaultCountryCode,
                isLoading: !field.fetchInfo.response
            };
        },
        props: ({ t }) => {
            return {
                formatCreateLabel: formatCreateLabel(t)
            };
        },
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value?.value),
        fetchInfo: {
            fetcher: citiesApi.fetch.bind(citiesApi),
            params: ({ props, entity }) => {
                const countryCode = entity.getIn([
                    'locationInfo',
                    'location',
                    'address',
                    'countryCode'
                ]);
                return { countryId: countryCode || props.defaultCountryCode };
            },
            response: null
        }
    },
    {
        accessor: 'locationInfo.location.address.state',
        dependentAccessors: [
            'locationInfo.location.latLng',
            'locationInfo.location.latLng.lat',
            'locationInfo.location.latLng.lng'
        ],
        supportsOnBlur: true,
        label: 'outletForm.address.state',
        type: FieldTypeEnum.Text,
        placeholder: 'outletForm.address.statePlaceholder',
        halfWidth: true,
        testId: outletMasterIds.addNewOutlet.inputAddress.inputState
    },
    {
        accessor: 'locationInfo.location.address.pincode',
        dependentAccessors: [
            'locationInfo.location.latLng',
            'locationInfo.location.latLng.lat',
            'locationInfo.location.latLng.lng'
        ],
        supportsOnBlur: true,
        testId: outletMasterIds.addNewOutlet.inputAddress.inputPincode,
        label: 'outletForm.address.pincode',
        type: FieldTypeEnum.Pincode,
        halfWidth: true,
        placeholder: 'outletForm.address.pincodePlaceholder',
        options: ({ field, props }) => {
            return {
                data: field.fetchInfo.response,
                countryCode: props.defaultCountryCode,
                isLoading: !field.fetchInfo.response
            };
        },
        props: ({ t }) => {
            return {
                formatCreateLabel: formatCreateLabel(t)
            };
        },
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value?.value),
        fetchInfo: {
            fetcher: pincodesApi.fetch.bind(pincodesApi),
            params: ({ props, entity }) => {
                const countryCode = entity.getIn([
                    'locationInfo',
                    'location',
                    'address',
                    'countryCode'
                ]);
                return { countryId: countryCode || props.defaultCountryCode };
            },
            response: null
        }
    },
    {
        accessor: 'locationInfo.location.address.countryCode',
        dependentAccessors: [
            'locationInfo.location.address.city',
            'locationInfo.location.address.pincode'
        ],
        label: 'outletForm.address.country',
        type: FieldTypeEnum.Select,
        required: true,
        options: CountryHelper.getCountryCodeOptions(),
        testId: outletMasterIds.addNewOutlet.inputAddress.inputCountryCode,
        placeholder: 'outletForm.address.countryPlaceholder',
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        },
        extraProps: ({ field, props }) => {
            return {
                isSearchable: true,
                value: field.value || props.defaultCountryCode
            };
        }
    },
    {
        accessor: 'locationInfo.location.latLng',
        dependentAccessors: [
            'locationInfo.location.latLng.lat',
            'locationInfo.location.latLng.lng'
        ],
        type: FieldTypeEnum.Map,
        halfWidth: true,
        extraProps: ({ entity }) => {
            return {
                labelInfo: {
                    label: 'outletForm.locationMap',
                    autoDetect: 'outletForm.autoDetectLocation'
                },
                address: entity.getIn(['locationInfo', 'location', 'address'])
            };
        }
    },
    {
        accessor: 'locationInfo.location.latLng.lat',
        dependentAccessors: ['locationInfo.location.latLng'],
        label: 'outletForm.lat',
        placeholder: 'outletForm.latPlaceholder',
        testId: 'outletLat',
        type: FieldTypeEnum.Text,
        halfWidth: true
    },
    {
        accessor: 'locationInfo.location.latLng.lng',
        dependentAccessors: ['locationInfo.location.latLng'],
        label: 'outletForm.lng',
        placeholder: 'outletForm.lngPlaceholder',
        testId: 'outletLng',
        type: FieldTypeEnum.Text,
        halfWidth: true
    },
    {
        accessor: 'locationInfo.location.type',
        testId: locationMasterIDs.addNewLocation.selectType,
        label: 'outletForm.locationType',
        type: FieldTypeEnum.Select,
        required: true,
        placeholder: 'outletForm.locationTypePlaceholder',
        getLocationFilters: locationCodeInputFilters,
        options: () =>
            OutletLocationTypeEnum.getOptions({
                translationKey: 'masterOutletProperties.outletLocationTypes'
            }),
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        }
    },
    {
        accessor: 'channels',
        type: FieldTypeEnum.Select,
        label: 'outletForm.channels',
        placeholder: 'outletForm.outletChannelPlaceholder',
        testId: outletMasterIds.addNewOutlet.inputOutletChannel,
        options: ({ props }) =>
            props.entitySettings?.getPjpChannelField() || [],
        onChangeHandler: ({ field, entity, value }) => {
            if (value) {
                const salesChannelName = value.label;
                const salesChannelId = value.value;
                const updatedChannel = ChannelData.fromJS({
                    id: salesChannelId,
                    name: salesChannelName
                });

                return entity.setIn(
                    field.absoluteAccessor,
                    Immutable.List([updatedChannel])
                );
            }
            return entity.setIn(field.absoluteAccessor, Immutable.List([]));
        },
        getProcessedValueForField: ({ entity }) => {
            return entity.getIn(['channels'])?.get(0)?.name;
        }
    },
    {
        accessor: 'timeSlots',
        label: 'outletForm.slots',
        required: true,
        type: FieldTypeEnum.MultiDaySlots,
        testId: 'addSlots',
        extraProps: ({ t }) => {
            return {
                translationsObject: {
                    add: t('outletForm.addSlots'),
                    change: t('outletForm.changeSlots')
                }
            };
        },
        getProcessedValueForField: ({ entity }) =>
            entity.initializeWeekSlots().timeSlots
    },
    {
        accessor: 'transactionDuration',
        label: 'outletForm.transactionDuration',
        helpText: 'outletForm.tatTooltip',
        halfWidth: true,
        type: FieldTypeEnum.TransactionDuration,
        testId: outletMasterIds.addNewOutlet.inputDuration,
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value * 60)
    },
    {
        accessor: 'zoneId',
        label: 'outletForm.zone',
        helpText: 'outletForm.zoneTooltip',
        placeholder: 'outletForm.addZone',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'serviceMix',
        type: FieldTypeEnum.Select,
        label: 'outletForm.serviceMix',
        placeholder: 'outletForm.serviceMixPlaceholder',
        testId: outletMasterIds.addNewOutlet.inputServiceMix,
        options: ({ props }) => {
            return getPossibleValues({ key: 'serviceMix', props });
        },
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value || null);
        }
    },
    {
        accessor: 'historicalData.currentRiderId',
        label: 'outletForm.currentRiderId',
        testId: 'currentRiderId',
        helpText: 'outletForm.currentRiderTooltip',
        type: FieldTypeEnum.RiderIdInput,
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.riderId || null);
        },
        extraProps: ({ entity }) => {
            return {
                getRiderFilters: input => riderIdInputFilters({ entity, input })
            };
        }
    },
    {
        accessor: 'historicalData.currentBeatId',
        dependentAccessors: ['historicalData.isFrozenToBeat'],
        label: 'outletForm.currentPjpName',
        placeholder: 'outletForm.pjpNamePlaceholder',
        helpText: 'outletForm.currentPjpNameTooltip',
        testId: outletMasterIds.addNewOutlet.inputCurPJPName,
        required: false,
        type: FieldTypeEnum.Text,
        onChangeHandler: ({ entity, field, value }) => {
            const isFrozenToBeat = !!entity.getIn([
                'historicalData',
                'isFrozenToBeat'
            ]);
            const updatedEntity = entity.setIn(field.absoluteAccessor, value);
            return value
                ? updatedEntity.setIn(
                      ['historicalData', 'frozenTourId'],
                      isFrozenToBeat ? value : null
                  )
                : updatedEntity.setIn(
                      ['historicalData', 'isFrozenToBeat'],
                      false
                  );
        }
    },
    {
        accessor: 'historicalData.currentPjpType',
        label: 'outletForm.currentPjpType',
        helpText: 'outletForm.currentPjpTypeTooltip',
        placeholder: 'outletForm.currentPjpTypePlaceholder',
        testId: 'currentPjpType',
        type: FieldTypeEnum.Select,
        options: ({ props }) => {
            return getPossibleValues({ key: 'currentPjpType', props });
        },
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value || null);
        }
    },
    {
        accessor: 'historicalData.currentPjpDayInfo.dayOfWeek',
        label: 'outletForm.currentPjpDayInfo',
        helpText: 'outletForm.currentPJPDayTooltip',
        testId: 'currentPjpDayInfo',
        component: DayWeekSelector,
        extraProps: ({ entity, field, t }) => {
            return {
                t,
                multiSelectDays: false,
                chosenDays: [entity.getIn(field.absoluteAccessor)]
            };
        },
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(
                field.absoluteAccessor,
                value?.days?.[0] || null
            );
        }
    },
    {
        accessor: 'historicalData.currentPjpDayInfo.week',
        label: 'outletForm.currentPJPWeek',
        helpText: 'outletForm.currentPJPWeekTooltip',
        placeholder: 'outletForm.currentPJPWeekPlaceholder',
        type: FieldTypeEnum.Select,
        options: weekInfo,
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value || null);
        },
        getProcessedValueForField: ({ entity, field }) => {
            return entity.getIn(field.absoluteAccessor) || null;
        }
    },
    {
        accessor: 'holidays',
        label: 'outletForm.holidays',
        helptext: 'outletForm.holidaysHelpText',
        component: HolidaySelector,
        extraProps: ({ entity, t }) => {
            const holidays = entity.get('holidays');
            return { holidays: holidays || [], t };
        },
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, Immutable.List(value));
        }
    },
    {
        accessor: 'historicalData.recentRevenue.amount.amount',
        label: ({ props, t }) => {
            const revenueMonthCount = props?.revenueMonthCount || 3;
            return t('outletForm.revenueGeneratedMonth', {
                count: revenueMonthCount,
                revenueMonthCount,
                symbol: getSymbolFromCurrency(props?.defaultCurrency)
            });
        },
        placeholder: 'outletForm.revenueGeneratedPlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'historicalData.averageSkusSold',
        label: 'outletForm.skuSales',
        placeholder: 'outletForm.skuSalesPlaceholder',
        type: FieldTypeEnum.Text,
        extraProps: () => {
            return {
                min: '0',
                pattern: '\\d*'
            };
        }
    },
    {
        accessor: 'historicalData.isFrozenToRider',
        label: 'outletForm.isFrozenToRider',
        helpText: 'outletForm.isFrozenToRiderTooltip',
        component: RiderIdInputWithSwitch,
        extraProps: ({ entity, field, props, t }) => {
            const checked = !!entity.getIn(field.absoluteAccessor);
            const rider =
                entity.getIn(['historicalData', 'frozenRiderId']) || null;
            return {
                t,
                type: 'riderIdInput',
                fieldTitle: t('outletForm.frozenToFE'),
                checked,
                clientId: props.clientId,
                rider,
                getValueFromProps: props => {
                    return props.rider;
                },
                getRiderFilters: input =>
                    riderIdInputFilters({ entity, input }),
                switchLabel: checked
                    ? t('outletTable.saleMadeRecentlyText')
                    : t('outletTable.noSaleMadeRecentlyText')
            };
        },
        onChangeHandler: ({ field, entity, value }) => {
            const riderId = value?.value?.riderId
                ? value?.value?.riderId
                : value?.value;
            const updatedEntity = entity.setIn(
                ['historicalData', 'frozenRiderId'],
                value?.checked ? riderId : null
            );
            return updatedEntity.setIn(
                field.absoluteAccessor,
                value?.checked || null
            );
        }
    },
    {
        accessor: 'historicalData.isFrozenToBeat',
        label: 'outletForm.isFrozenToBeat',
        helpText: 'outletForm.isFrozenToBeatTooltip',
        component: FieldWithSwitch,
        extraProps: ({ entity, field, t }) => {
            const checked = entity.getIn(field.absoluteAccessor);
            return {
                t,
                label: '',
                type: 'text',
                element: 'input',
                fieldTitle: t('outletForm.frozenToBeat'),
                checked,
                placeholder: t('outletForm.frozenToBeatPlaceholder'),
                value:
                    entity.getIn(['historicalData', 'frozenTourId']) ||
                    entity.getIn(['historicalData', 'currentBeatId']) ||
                    null,
                switchLabel: checked
                    ? t('outletTable.saleMadeRecentlyText')
                    : t('outletTable.noSaleMadeRecentlyText')
            };
        },
        onChangeHandler: ({ field, entity, value }) => {
            const updatedEntity = value?.checked
                ? entity.setIn(
                      ['historicalData', 'frozenTourId'],
                      value?.value || null
                  )
                : entity;
            return updatedEntity.setIn(
                field.absoluteAccessor,
                value?.checked || null
            );
        }
    },
    {
        accessor: 'historicalData.serviceBySameRider',
        label: 'outletForm.serviceBySameRider',
        helpText: 'outletForm.serviceBySameRiderTooltip',
        type: FieldTypeEnum.Boolean,
        extraProps: ({ field, t }) => {
            return {
                label: field.value
                    ? t('outletTable.saleMadeRecentlyText')
                    : t('outletTable.noSaleMadeRecentlyText')
            };
        }
    },
    {
        accessor: 'visitFrequencyInfo',
        label: 'outletForm.visitFrequencyInfo',
        component: VisitFrequencyInfo,
        extraProps: ({ entity, field, t }) => {
            const visitFrequencyInfo = entity.getIn([
                ...field.absoluteAccessor
            ]);
            const checked =
                visitFrequencyInfo.spaceVisitsEvenly ||
                DEFAULT_VISIT_FREQUENCY_INFO.spaceVisitsEvenly;
            return {
                durationTitle: t('outletForm.duration'),
                repeatCountTitle: t('outletForm.repeatCount'),
                spaceVisitsEvenlyTitle: t('outletForm.spaceVisitsEvenly'),
                planningDurationInfo: getWeekValues(t),
                repeatCount: entity.getIn([
                    ...field.absoluteAccessor,
                    'repeatCount'
                ]),
                defaultRepeatCount: DEFAULT_VISIT_FREQUENCY_INFO.repeatCount,
                repeatCountError: entity
                    .getIn(field.absoluteAccessor)
                    .getValidationMessage('repeatCount'),
                duration: entity.getIn([...field.absoluteAccessor, 'duration']),
                defaultDuration: DEFAULT_VISIT_FREQUENCY_INFO.duration,
                planDurationPlaceholder: t(
                    'outletForm.planDurationPlaceholder'
                ),
                spaceVisitsEvenly: checked,
                spaceVisitsEvenlyLabel: checked
                    ? t('outletTable.saleMadeRecentlyText')
                    : t('outletTable.noSaleMadeRecentlyText'),
                spaceVisitsEvenlyHelpText: t(
                    'outletForm.spaceVisitsEvenlyHelpText'
                ),
                durationTitleHelpText: t('outletForm.durationTitleHelpText'),
                repeatCountHelpText: t('outletForm.repeatCountHelpText')
            };
        },
        onChangeHandler: ({ entity, value }) => {
            let newVisitFrequncyInfo = entity.visitFrequencyInfo;
            if ('duration' in value) {
                newVisitFrequncyInfo = newVisitFrequncyInfo.setDuration(
                    value.duration
                );
            }

            if ('repeatCount' in value) {
                newVisitFrequncyInfo = newVisitFrequncyInfo.setRepeatCount(
                    value.repeatCount
                );
            }

            if ('spaceVisitsEvenly' in value) {
                newVisitFrequncyInfo = newVisitFrequncyInfo.setSpaceVisitsEvenly(
                    value.spaceVisitsEvenly
                );
            }
            return entity.setVisitFrequencyInfo(newVisitFrequncyInfo);
        }
    },
    {
        accessor: 'mandatoryDays',
        helpText: 'outletForm.mandatoryDaysHelpText',
        label: 'outletForm.mandatoryDays',
        component: DayWeekSelector,
        extraProps: ({ entity, field, t }) => {
            let chosenDays = entity.getIn(field.absoluteAccessor);
            chosenDays = chosenDays ? chosenDays.toArray() : [];
            return {
                t,
                multiSelectDays: true,
                chosenDays
            };
        },
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(
                field.absoluteAccessor,
                Immutable.List(value?.days || [])
            );
        }
    },
    {
        accessor: 'historicalData.shouldKeep',
        label: 'outletForm.shouldKeep',
        type: FieldTypeEnum.Boolean,
        extraProps: ({ field, t }) => {
            return {
                label: field.value
                    ? t('outletTable.keepText')
                    : t('outletTable.removeText')
            };
        }
    },
    {
        accessor: 'historicalData.saleMadeRecently',
        label: 'outletForm.saleMadeRecently',
        type: FieldTypeEnum.Boolean,
        extraProps: ({ field, t }) => {
            return {
                label: field.value
                    ? t('outletTable.saleMadeRecentlyText')
                    : t('outletTable.noSaleMadeRecentlyText')
            };
        }
    },
    {
        accessor: 'historicalData.isDuplicateParty',
        label: 'outletForm.duplicateParty',
        type: FieldTypeEnum.Boolean,
        extraProps: ({ field, t }) => {
            return {
                label: field.value
                    ? t('outletTable.isDuplicatePartyText')
                    : t('outletTable.isNotDuplicatePartyText')
            };
        }
    },
    {
        accessor: 'historicalData.isNew',
        label: 'outletForm.isNew',
        type: FieldTypeEnum.Boolean,
        extraProps: ({ field, t }) => {
            return {
                label: field.value
                    ? t('outletTable.isNewText')
                    : t('outletTable.isNotNewText')
            };
        }
    },
    {
        accessor: 'averageVolume',
        label: 'outletForm.volume',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'customProperties',
        type: FieldTypeEnum.CustomFields,
        extraProps: () => {
            return {
                settingsName: 'outletSettings'
            };
        }
    }
];

const OUTLET_INITIAL_FETCH_FIELDS = [
    'salesCategoryId',
    'locationInfo.location.address.city',
    'locationInfo.location.address.pincode'
];

const SingleOutletEditContainer = entityMasterSingleEditFactory({
    MasterEntity: MasterOutlet,
    MasterActions: OutletActions,
    EntityFetchAPI: outletApi,
    extraApiParams: {
        include: IncludeTypeEnums.SalesCategory
    },
    INITIAL_FETCH_FIELDS: OUTLET_INITIAL_FETCH_FIELDS,
    FIELDS: OUTLET_FIELDS,
    options: {
        ENTITY_NAME_ID,
        PARAM_ID: 'outletId',
        ENTITY_SETTINGS_NAME: 'outletSettings'
    },
    mapEntityStateToProps: ({ state, props, baseProps }) => {
        const teams = teamsSelector(state, props);
        const defaultCountryCenter = ClientSelectors.getCountryCenterFromLocaleSettings(
            state,
            props
        );
        return {
            teams,
            defaultCountryCenter,
            defaultCountryCode: baseProps?.localeSettings?.countryCode,
            defaultCurrency: baseProps?.localeSettings?.currency
        };
    }
});

export default SingleOutletEditContainer;
